import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import { or, eq } from 'tio-ui/utilities';
import { dropTask } from 'ember-concurrency';
import { PhReceipt, PhCloudArrowUp } from 'ember-phosphor-icons';
import Modal from 'tio-reimagined/components/modal';
import Button from 'tio-reimagined/components/button';
import Chip from 'tio-reimagined/components/chip';
import Text from 'tio-reimagined/components/text';
import FileUploader from 'tio-common/components/file-uploader';
import CurrencySuperscriptCents from 'tio-common/components/currency-superscript-cents';
import { t } from 'ember-intl';
import type IntlService from 'ember-intl/services/intl';
import type {
  CourseCashReceipt,
  CourseCashTransaction,
} from 'tio-common/types/course-cash-transaction';
import type { DigitalAssetDetailSignature } from 'tio-common/services/digital-assets';

interface ReceiptUploadModalSignature {
  Args: {
    isOpen: boolean;
    transaction: CourseCashTransaction | null;
    onClose: () => void;
    onUpload: (
      files: DigitalAssetDetailSignature[],
      transaction: CourseCashTransaction
    ) => Promise<void>;
    onRemoveReceipt?: (
      receipt: CourseCashReceipt,
      transaction: CourseCashTransaction
    ) => Promise<void>;
  };
  Element: HTMLDivElement;
}

export default class CourseCashReceiptUploadModal extends Component<ReceiptUploadModalSignature> {
  @service declare intl: IntlService;
  @tracked selectedFiles: Array<{ name: string; details: DigitalAssetDetailSignature }> = [];
  @tracked isErrorModalOpen = false;
  @tracked errorMessage: string | null = null;
  @tracked isConfirmRemoveOpen = false;
  @tracked receiptToRemove: CourseCashReceipt | null = null;

  get formattedDate() {
    if (!this.args.transaction) return '';
    const date = new Date(this.args.transaction.transactionDate + 'T00:00:00');
    return this.intl.formatDate(date, {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    });
  }

  get formattedDueDate() {
    if (!this.args.transaction?.receiptDueDate) return '';
    const date = new Date(this.args.transaction.receiptDueDate + 'T00:00:00');
    return this.intl.formatDate(date, {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  }

  get canUpload() {
    return this.selectedFiles.length > 0 && !this.handleUpload.isRunning;
  }

  get isDisabled() {
    // For HTML disabled attribute: return true to disable, undefined to enable
    // (presence of disabled attribute disables the button)
    return this.canUpload && !this.handleConfirmRemove.isRunning ? undefined : true;
  }

  get existingReceipts() {
    return this.args.transaction?.receipts ?? [];
  }

  get hasVerifiedReceipt() {
    return this.existingReceipts.some((r) => r.status === 'verified');
  }

  get hasPendingReceipt() {
    return this.existingReceipts.some((r) => r.status === 'pending_verification');
  }

  // Stable ID for the file input — allows the Replace button to trigger
  // it via a click handler without needing a DOM ref or yielded uniqueId.
  get fileInputId() {
    return 'course-cash-receipt-file-input';
  }

  chipIntentForStatus = (status: CourseCashReceipt['status']) => {
    switch (status) {
      case 'verified':
        return 'success';
      case 'rejected':
        return 'danger';
      case 'changes_needed':
        return 'warning';
      case 'pending_verification':
      default:
        return 'warning';
    }
  };

  labelForStatus = (status: CourseCashReceipt['status']) => {
    switch (status) {
      case 'verified':
        return this.intl.t('tas.course_cash.verified');
      case 'rejected':
        return this.intl.t('tas.course_cash.rejected');
      case 'changes_needed':
        return this.intl.t('tas.course_cash.changes_needed');
      case 'pending_verification':
      default:
        return this.intl.t('tas.course_cash.in_review');
    }
  };

  handleFileUploaded = (fileDetails: DigitalAssetDetailSignature) => {
    // Replace (not append) — only one receipt per transaction is allowed
    this.selectedFiles = [{ name: fileDetails.filename, details: fileDetails }];
  };

  handleRemoveFile = (fileToRemove: { name: string; details: DigitalAssetDetailSignature }) => {
    this.selectedFiles = this.selectedFiles.filter((f) => f !== fileToRemove);
  };

  handleUpload = dropTask(async () => {
    if (this.selectedFiles.length === 0 || !this.args.transaction) return;

    try {
      // Extract just the S3 details to pass to the handler
      const fileDetails = this.selectedFiles.map((f) => f.details);
      await this.args.onUpload(fileDetails, this.args.transaction);
      this.selectedFiles = [];
      this.args.onClose();
    } catch (error) {
      console.error('Failed to upload receipt:', error);
      this.openErrorModal(this.intl.t('tas.course_cash.receipt_upload_failed'));
    }
  });

  handleClose = () => {
    if (!this.handleUpload.isRunning && !this.handleConfirmRemove.isRunning) {
      this.selectedFiles = [];
      this.args.onClose();
    }
  };

  handleRequestRemoveReceipt = (receipt: CourseCashReceipt) => {
    this.receiptToRemove = receipt;
    this.isConfirmRemoveOpen = true;
  };

  handleCloseConfirmRemove = () => {
    this.isConfirmRemoveOpen = false;
    this.receiptToRemove = null;
  };

  handleConfirmRemove = dropTask(async () => {
    if (!this.args.transaction || !this.args.onRemoveReceipt || !this.receiptToRemove) return;

    try {
      await this.args.onRemoveReceipt(this.receiptToRemove, this.args.transaction);
      this.handleCloseConfirmRemove();
    } catch (error) {
      console.error('Failed to remove receipt:', error);
      this.openErrorModal(
        this.intl.t('tas.course_cash.receipt_upload_modal.remove_receipt_failed')
      );
    }
  });

  openErrorModal = (message: string) => {
    this.errorMessage = message;
    this.isErrorModalOpen = true;
  };

  handleCloseErrorModal = () => {
    this.isErrorModalOpen = false;
    this.errorMessage = null;
  };

  handleReplaceClick = () => {
    const input = document.getElementById(this.fileInputId) as HTMLInputElement | null;
    input?.click();
  };

  <template>
    <Modal
      @isOpen={{@isOpen}}
      @onClose={{this.handleClose}}
      @closeOnOutsideClick={{false}}
      @size="xl"
    >
      <:header>
        <div class="flex gap-1.5 items-center">
          <PhReceipt @size="20" @weight="regular" />
          <span class="font-semibold text-lg">{{t
              "tas.course_cash.receipt_upload_modal.title"
            }}</span>
        </div>
      </:header>

      <:body>
        <div class="tio-col-loose w-full">
          {{! Due date notice }}
          {{#if this.formattedDueDate}}
            <Text @size="base">
              {{t
                "tas.course_cash.receipt_upload_modal.upload_by_date"
                dueDate=this.formattedDueDate
                htmlSafe=true
              }}
            </Text>
          {{/if}}

          {{! Unverified Transaction Label }}
          <div class="flex flex-col">
            <Text @size="xs" @weight="semibold" @variant="muted" class="uppercase tracking-wide">
              {{t "tas.course_cash.receipt_upload_modal.unverified_transaction"}}
            </Text>
          </div>

          {{! Transaction Details }}
          {{#if @transaction}}
            <div class="flex gap-6 items-start pb-4 w-full border-b tio-border-default">
              <div class="flex items-center p-1 rounded-md shrink-0 w-8 h-8 relative">
                <PhReceipt @size="24" @weight="regular" />
                <div
                  class="absolute tio-bg-surface flex items-center p-0.5 rounded-xl right-0 top-0"
                >
                  <div class="tio-bg-danger rounded w-2 h-2"></div>
                </div>
              </div>
              <div class="flex-1 tio-row-between">
                <div class="flex flex-col">
                  <Text @size="xs">{{this.formattedDate}}</Text>
                  <Text @size="sm">{{@transaction.merchantName}}</Text>
                </div>
                <div class="text-right font-semibold">
                  <CurrencySuperscriptCents @amountCents={{@transaction.amount}} />
                </div>
              </div>
            </div>
          {{/if}}

          {{! Uploaded Receipts }}
          {{#if this.existingReceipts.length}}
            <div class="tio-col-tight w-full">
              <Text @size="xs" @weight="semibold" @variant="muted" class="uppercase tracking-wide">
                {{t "tas.course_cash.receipt_upload_modal.uploaded_receipts"}}
              </Text>
              {{#each this.existingReceipts as |receipt|}}
                <div class="tio-row-between w-full p-2 tio-bg-sunken rounded">
                  <div class="tio-col-extra-tight min-w-0">
                    <div class="tio-row-tight">
                      <Text @size="sm" class="truncate">{{receipt.filename}}</Text>
                      {{#if receipt.approvableState}}
                        <Chip
                          @intent={{this.chipIntentForStatus receipt.status}}
                          @appearance="faded"
                          @size="sm"
                          data-test-receipt-status-chip
                        >
                          {{this.labelForStatus receipt.status}}
                        </Chip>
                      {{/if}}
                    </div>
                    {{#if receipt.receiptUrl}}
                      <a
                        href={{receipt.receiptUrl}}
                        target="_blank"
                        rel="noopener noreferrer"
                        class="tio-copy text-xs"
                        data-test-view-receipt-link
                      >
                        {{t "tas.course_cash.receipt_upload_modal.view_receipt"}}
                      </a>
                    {{/if}}
                    {{#if (eq receipt.status "changes_needed")}}
                      <Text @size="xs" @variant="error" data-test-receipt-feedback>
                        {{t "tas.course_cash.receipt_upload_modal.changes_requested"}}
                      </Text>
                    {{/if}}
                  </div>
                  {{#unless (eq receipt.status "verified")}}
                    {{#if @onRemoveReceipt}}
                      {{#if (eq receipt.status "pending_verification")}}
                        <Button
                          @intent="primary"
                          @appearance="minimal"
                          @size="sm"
                          @isDisabled={{or
                            this.handleUpload.isRunning
                            this.handleConfirmRemove.isRunning
                          }}
                          {{on "click" this.handleReplaceClick}}
                          data-test-replace-receipt-button
                        >
                          {{t "tas.course_cash.receipt_upload_modal.replace"}}
                        </Button>
                      {{else}}
                        <Button
                          @intent="danger"
                          @appearance="minimal"
                          @size="sm"
                          @isDisabled={{or
                            this.handleUpload.isRunning
                            this.handleConfirmRemove.isRunning
                          }}
                          {{on "click" (fn this.handleRequestRemoveReceipt receipt)}}
                          data-test-remove-receipt-button
                        >
                          {{t "tas.course_cash.receipt_upload_modal.remove"}}
                        </Button>
                      {{/if}}
                    {{/if}}
                  {{/unless}}
                </div>
              {{/each}}
            </div>
          {{/if}}

          {{! Upload Area - one receipt per transaction }}
          {{#if this.hasVerifiedReceipt}}
            <Text @size="sm" @variant="muted" data-test-verified-receipt-notice>
              {{t "tas.course_cash.receipt_upload_modal.verified_receipt_no_upload"}}
            </Text>
          {{else}}
            {{! Upload Area - Using block form to avoid z-index issues with Modal }}
            <FileUploader @didUploadFile={{this.handleFileUploaded}} as |uploader|>
              <input
                id={{this.fileInputId}}
                type="file"
                accept="image/*,.pdf"
                {{uploader.queue.selectFile}}
                class="hidden"
                disabled={{or
                  uploader.queue.files.length
                  this.handleUpload.isRunning
                  this.handleConfirmRemove.isRunning
                }}
              />
              {{#unless this.hasPendingReceipt}}
                {{! Drag-drop zone — hidden when pending receipt exists; Replace button serves as entry point }}
                <label
                  for={{this.fileInputId}}
                  class="tio-place-center w-full h-48 border-2 tio-border-default border-dashed rounded-lg cursor-pointer tio-bg-sunken hover:tio-bg-sunken-hover"
                >
                  <div class="tio-place-center pt-5 pb-6">
                    <PhCloudArrowUp @size="32" class="mb-2 tio-fg-muted" />
                    <p class="mb-2 text-sm tio-fg-muted">
                      <span class="font-semibold">{{t
                          "tas.course_cash.receipt_upload_modal.select_file"
                        }}</span>
                      {{t "tas.course_cash.receipt_upload_modal.drag_drop"}}
                    </p>
                    <p class="text-xs tio-fg-disabled">{{t
                        "tas.course_cash.receipt_upload_modal.file_types"
                      }}</p>
                  </div>
                </label>
              {{/unless}}
            </FileUploader>

            {{! Selected Files }}
            {{#if this.selectedFiles.length}}
              <div class="tio-col-tight w-full">
                <Text
                  @size="xs"
                  @weight="semibold"
                  @variant="muted"
                  class="uppercase tracking-wide"
                >
                  {{t "tas.course_cash.receipt_upload_modal.selected_files"}}
                </Text>
                {{#each this.selectedFiles as |file|}}
                  <div class="tio-row-between w-full p-2 tio-bg-sunken rounded">
                    <Text @size="sm">{{file.name}}</Text>
                    <Button
                      @intent="default"
                      @appearance="minimal"
                      @size="sm"
                      {{on "click" (fn this.handleRemoveFile file)}}
                    >
                      {{t "tas.course_cash.receipt_upload_modal.remove"}}
                    </Button>
                  </div>
                {{/each}}
              </div>
            {{/if}}
          {{/if}}
        </div>
      </:body>

      <:footer>
        <div class="tio-row-end gap-4 w-full">
          <Button
            @intent="default"
            @appearance="outlined"
            @isDisabled={{or this.handleUpload.isRunning this.handleConfirmRemove.isRunning}}
            {{on "click" this.handleClose}}
          >
            {{t "tas.course_cash.receipt_upload_modal.cancel"}}
          </Button>
          {{#unless this.hasVerifiedReceipt}}
            <Button
              @intent="primary"
              {{on "click" this.handleUpload.perform}}
              @isDisabled={{this.isDisabled}}
            >
              {{#if this.handleUpload.isRunning}}
                {{t "tas.course_cash.receipt_upload_modal.uploading"}}
              {{else}}
                {{t "tas.course_cash.receipt_upload_modal.upload"}}
              {{/if}}
            </Button>
          {{/unless}}
        </div>
      </:footer>
    </Modal>

    <Modal @isOpen={{this.isErrorModalOpen}} @onClose={{this.handleCloseErrorModal}} @size="sm">
      <:header>
        <span class="font-semibold text-lg">
          {{t "common.error"}}
        </span>
      </:header>
      <:body>
        <Text @size="sm">{{this.errorMessage}}</Text>
      </:body>
      <:footer>
        <div class="tio-flex-end w-full">
          <Button
            @intent="primary"
            @appearance="outlined"
            {{on "click" this.handleCloseErrorModal}}
          >
            {{t "common.close"}}
          </Button>
        </div>
      </:footer>
    </Modal>

    <Modal
      @isOpen={{this.isConfirmRemoveOpen}}
      @onClose={{this.handleCloseConfirmRemove}}
      @size="sm"
    >
      <:header>
        <span class="font-semibold text-lg">
          {{t "tas.course_cash.receipt_upload_modal.remove"}}
        </span>
      </:header>
      <:body>
        <Text @size="sm">
          {{t "tas.course_cash.receipt_upload_modal.confirm_remove_receipt"}}
        </Text>
      </:body>
      <:footer>
        <div class="tio-flex-end gap-3 w-full">
          <Button
            @intent="default"
            @appearance="outlined"
            @isDisabled={{this.handleConfirmRemove.isRunning}}
            {{on "click" this.handleCloseConfirmRemove}}
          >
            {{t "tas.course_cash.receipt_upload_modal.cancel"}}
          </Button>
          <Button
            @intent="danger"
            @isDisabled={{this.handleConfirmRemove.isRunning}}
            {{on "click" this.handleConfirmRemove.perform}}
          >
            {{t "tas.course_cash.receipt_upload_modal.remove"}}
          </Button>
        </div>
      </:footer>
    </Modal>
  </template>
}
